import React, { useEffect, useState } from "react";
import { formatDateOnly } from "helpers/dateOnly";
import { Box, Flex } from "theme-ui";
import { useNavigate } from "react-router-dom";
import * as DashboardAPI from "api/Dashboard";
import P from "components/typography/P";
import { ProductsIcon } from "components/svg";
import { themevals } from "theme/themevals";
import moment from "moment";

interface UpcomingDelivery {
  id: string;
  poId: string;
  poNumber: string;
  projectName: string;
  scheduleDate: string;
  scheduleTime: string | null;
  quantity: number;
  unit: string;
  status: "accepted" | "sent";
  address: string;
}

const UpcomingDeliveries: React.FC = () => {
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState<UpcomingDelivery[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDeliveries = async () => {
    try {
      setLoading(true);
      const data = await DashboardAPI.getProviderUpcomingDeliveries(5);
      if (data) {
        setDeliveries(data.deliveries);
        setTotalCount(data.totalCount);
      } else {
        setError("Failed to load upcoming deliveries");
      }
    } catch (err) {
      console.error("Error fetching upcoming deliveries:", err);
      setError("Failed to load upcoming deliveries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliveries();
  }, []);

  const handleDeliveryClick = (poId: string) => {
    navigate(`/purchase-orders/${poId}`);
  };

  const getStatusColors = (status: "accepted" | "sent") => {
    if (status === "accepted") {
      return {
        bgColor: "#DDE8D2",
        textColor: themevals.colors.success,
      };
    }
    return {
      bgColor: "#FEF3E0",
      textColor: themevals.colors.warning,
    };
  };

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: "white",
          border: "1px solid",
          borderColor: themevals.colors.inputOutline,
          borderRadius: "8px",
          boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px",
          width: "100%",
        }}
      >
        <P>Loading...</P>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          backgroundColor: "white",
          border: "1px solid",
          borderColor: themevals.colors.inputOutline,
          borderRadius: "8px",
          boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px",
          width: "100%",
        }}
      >
        <P color={themevals.colors.danger}>{error}</P>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "white",
        border: "1px solid",
        borderColor: themevals.colors.inputOutline,
        borderRadius: "8px",
        boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      {/* Header */}
      <Flex
        sx={{
          padding: "16px 24px",
          borderBottom: "1px solid",
          borderColor: themevals.colors.inputOutline,
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: "76px",
          flexShrink: 0,
        }}
      >
        <P fontFamily="Poppins" fontSize="14px" fontWeight="600" marginBottom="0" marginTop="0">
          Upcoming Deliveries ({totalCount})
        </P>
      </Flex>

      {/* Content */}
      <Box sx={{ padding: "16px" }}>
        {deliveries.length === 0 ? (
          <Flex
            sx={{
              alignItems: "center",
              justifyContent: "center",
              padding: "40px",
            }}
          >
            <P fontFamily="Poppins" fontSize="14px" color={themevals.colors.dkgrey}>
              No upcoming deliveries scheduled.
            </P>
          </Flex>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "16px",
            }}
          >
              {deliveries.map((delivery) => (
                <Flex
                  key={delivery.id}
                  onClick={() => handleDeliveryClick(delivery.poId)}
                  sx={{
                    border: "1px solid",
                    borderColor: themevals.colors.inputOutline,
                    borderRadius: "8px",
                    padding: "16px",
                    gap: "16px",
                    cursor: "pointer",
                  }}
                >
                {/* Date Display with Grey Background */}
                <Flex
                  sx={{
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "60px",
                    backgroundColor: themevals.colors.inputOutline,
                    borderRadius: "8px",
                    padding: "12px 8px",
                  }}
                >
                  <P
                    fontFamily="Poppins"
                    fontSize="24px"
                    fontWeight="700"
                    marginBottom="0"
                    marginTop="0"
                    lineHeight="1"
                    color={themevals.colors.black}
                  >
                    {formatDateOnly(delivery.scheduleDate, "DD")}
                  </P>
                  <P
                    fontFamily="Poppins"
                    fontSize="12px"
                    fontWeight="600"
                    marginBottom="0"
                    marginTop="4px"
                    color={themevals.colors.dkgrey}
                  >
                    {formatDateOnly(delivery.scheduleDate, "MMM").toUpperCase()}
                  </P>
                </Flex>

                {/* Delivery Info */}
                <Flex sx={{ flexDirection: "column", flex: 1, gap: "4px", justifyContent: "center" }}>
                  <P fontFamily="Poppins" fontSize="16px" fontWeight="600" marginBottom="0" marginTop="0">
                    {delivery.projectName}
                  </P>
                  <Flex sx={{ alignItems: "center", gap: "8px" }}>
                    <ProductsIcon fill={themevals.colors.dkgrey} width="16px" height="16px" />
                    <P
                      fontFamily="Poppins"
                      fontSize="14px"
                      marginBottom="0"
                      marginTop="0"
                      color={themevals.colors.dkgrey}
                    >
                      {delivery.quantity} {delivery.unit}
                    </P>
                  </Flex>
                </Flex>

                {/* Status Badge - Far Right */}
                <Flex sx={{ alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      display: "inline-block",
                      backgroundColor: getStatusColors(delivery.status).bgColor,
                      color: getStatusColors(delivery.status).textColor,
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: 500,
                      fontFamily: "Poppins",
                      textTransform: "capitalize",
                    }}
                  >
                    {delivery.status}
                  </Box>
                </Flex>
              </Flex>
              ))
            }
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UpcomingDeliveries;
